import React, { useCallback, useEffect, useState } from 'react';
import MissionSearchModal from '../MissionSearchModal';
import ConfirmModal from '../../FlowchartView/components/ConfirmModal';
import { ViewBar, CustomViewContextMenu, ToolbarRenderer } from './components';
import { useCustomViewActions } from './hooks';
import { isMissionSearchHotkey } from './utils/missionSearchHotkey';
import './styles/view-bar.css';

/**
 * ViewSwitcher Component
 * 
 * Renders the view tabs (Flow/Kanban/Table/Gantt/Todo) and the corresponding toolbar
 * for the currently active view. Also supports custom views via the [+] button.
 */
const ViewSwitcher = ({
  currentView,
  onViewChange,
  // Custom views support
  customViews = [],
  activeCustomViewId = null,
  onAddCustomView,
  onSelectCustomView,
  onRenameCustomView,
  onDuplicateCustomView,
  onDeleteCustomView,
  onReorderViews,
  // Mission search props
  onGoToMission,
  sprintId,
  statuses,
  // Multi-campaign mode - hides default tabs, shows only custom views
  isMultiCampaignMode = false,
  activeView = null,
  // Campaign variables — lives on the unified toolbar (null hides the button)
  onManageVariables = null,
  // All toolbar props (passed through to ToolbarRenderer)
  ...toolbarProps
}) => {
  const [isMissionSearchOpen, setIsMissionSearchOpen] = useState(false);
  const openMissionSearch = useCallback(() => setIsMissionSearchOpen(true), []);

  // Ctrl/Cmd+F opens the toolbar mission search in flow/kanban/table/etc.
  // Bubble phase so an open doc sheet's find (capture) still wins.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isMissionSearchHotkey(e)) return;
      e.preventDefault();
      openMissionSearch();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openMissionSearch]);

  const {
    isAddMenuOpen,
    contextMenu,
    editingViewId,
    editingName,
    deleteConfirm,
    addMenuRef,
    contextMenuRef,
    editInputRef,
    handleAddView,
    handleDefaultViewClick,
    handleCustomViewClick,
    handleCustomViewContextMenu,
    startEditing,
    saveEditedName,
    handleEditKeyDown,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    toggleAddMenu,
    closeContextMenu,
    setEditingName,
  } = useCustomViewActions({
    onAddCustomView,
    onSelectCustomView,
    onRenameCustomView,
    onDuplicateCustomView,
    onDeleteCustomView,
    onViewChange,
  });

  // Determine which view type is currently active (for toolbar rendering)
  const activeCustomView = customViews.find(v => v.id === activeCustomViewId);
  const effectiveViewType = activeCustomView ? activeCustomView.type : currentView;
  const isDefaultViewActive = !activeCustomViewId;

  return (
    <>
      <ViewBar
        currentView={currentView}
        isDefaultViewActive={isDefaultViewActive}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        editingViewId={editingViewId}
        editingName={editingName}
        editInputRef={editInputRef}
        addMenuRef={addMenuRef}
        isAddMenuOpen={isAddMenuOpen}
        onDefaultViewClick={handleDefaultViewClick}
        onCustomViewClick={handleCustomViewClick}
        onCustomViewContextMenu={handleCustomViewContextMenu}
        onEditingNameChange={setEditingName}
        onSaveEditedName={saveEditedName}
        onEditKeyDown={handleEditKeyDown}
        onDeleteClick={handleDeleteClick}
        onToggleAddMenu={toggleAddMenu}
        onAddView={handleAddView}
        onReorderViews={onReorderViews}
        isMultiCampaignMode={isMultiCampaignMode}
        activeView={activeView}
      />

      <CustomViewContextMenu
        contextMenu={contextMenu}
        contextMenuRef={contextMenuRef}
        onRename={startEditing}
        onDuplicate={onDuplicateCustomView}
        onDelete={onDeleteCustomView}
        onClose={closeContextMenu}
        totalViews={customViews?.length || 0}
      />

      <ToolbarRenderer
        effectiveViewType={effectiveViewType}
        onOpenMissionSearch={openMissionSearch}
        statuses={statuses}
        isMultiCampaignMode={isMultiCampaignMode}
        {...toolbarProps}
        onManageVariables={onManageVariables}
      />

      <MissionSearchModal
        isOpen={isMissionSearchOpen}
        onClose={() => setIsMissionSearchOpen(false)}
        tasks={toolbarProps.tasks}
        statuses={statuses}
        campaignId={sprintId}
        currentView={effectiveViewType}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        onGoToMission={onGoToMission}
        allSprints={toolbarProps.allSprints}
        showNestedItems={toolbarProps.todoShowNestedItems}
      />

      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete View"
        message={`Are you sure you want to delete "${deleteConfirm?.viewName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        icon={
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
        }
      />
    </>
  );
};

export default ViewSwitcher;
